import * as React from 'react';

import type { SxProps } from '@mui/system';
import {
    CenterCircularProgress,
    EmptyBox,
    MenuItemProps,
    NotificationsPreferences,
    UserAccountDetails,
} from '@europrocurement/flexy-components';
import { useSearchParams } from 'react-router-dom';
import { ModalMenuContext } from '@europrocurement/flexy-components/hooks/useModalMenuContext';
import { ManageAvatar, SocieteGroupList } from '@europrocurement/l2d-modules';
import ThemePreferences from '@europrocurement/flexy-components/components/molecules/ThemePreferences/ThemePreferences';
import { useUtilisateur } from '@europrocurement/l2d-modules/hooks/useUtilisateur';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import type { Utilisateur } from '@europrocurement/l2d-domain';

export type AvailableItems =
    | 'account_details'
    | 'themes'
    | 'notifications'
    | 'groups_dossiers'
    | 'groups_societes'
    | 'security'
    | 'user_structure'
    | 'members';

export type SetNewContentToDisplayProps = {
    contentToDisplayOnRender: AvailableItems;
};

type ContentState = 'loading' | 'ready' | 'no-content';

export const useUserPreferencesBack = () => {
    const [contentDisplayed, setContentDisplayed] = React.useState<string | undefined>(undefined);
    const [contentState, setContentState] = React.useState<ContentState>('loading');
    const [isTransitioning, setIsTransitioning] = React.useState<boolean>(false);
    const [user, setUser] = React.useState<Utilisateur | null>(null);
    const [isUserLoading, setIsUserLoading] = React.useState<boolean>(true);
    const hasFetchedUser = React.useRef(false);

    const [searchParams, setSearchParams] = useSearchParams();
    const modalMenuContext = React.useContext(ModalMenuContext);

    const { getUserByEmail } = useUtilisateur();
    const kc = UseKeycloakService();

    React.useEffect(() => {
        if (hasFetchedUser.current) {
            return undefined;
        }

        let isCancelled = false;

        const fetchUser = async () => {
            try {
                const email = kc.getEmail();

                if (!email) {
                    setIsUserLoading(false);
                    return;
                }
                const userData = await getUserByEmail(email);

                if (!isCancelled) {
                    setUser(userData);
                    hasFetchedUser.current = true;
                }
            } catch {
                if (!isCancelled) {
                    setUser(null);
                    hasFetchedUser.current = true;
                }
            } finally {
                if (!isCancelled) {
                    setIsUserLoading(false);
                }
            }
        };

        fetchUser();

        return () => {
            isCancelled = true;
        };
    }, [getUserByEmail, kc]);

    /**
     * Change le contenu affiché avec une transition
     */
    const setNewContentToDisplay = async ({
        contentToDisplayOnRender,
    }: SetNewContentToDisplayProps) => {
        setIsTransitioning(true);

        // Petit délai pour permettre l'affichage du loader
        setTimeout(() => {
            setContentDisplayed(contentToDisplayOnRender);
            setIsTransitioning(false);
        }, 1000);
    };

    /**
     * Initialise le contenu affiché en fonction des paramètres URL
     */
    React.useEffect(() => {
        const settingsTarget = searchParams.get('settingsTarget') as AvailableItems | null;
        const targetContent = settingsTarget || 'account_details';

        setNewContentToDisplay({ contentToDisplayOnRender: targetContent });
        setContentState('ready');
    }, [searchParams, setSearchParams]);

    /**
     * Réinitialise activeTree à chaque changement de contenu pour éviter les conflits d'état
     * Uniquement si le contexte est disponible
     */
    React.useEffect(() => {
        if (modalMenuContext?.setActiveTree) {
            modalMenuContext.setActiveTree([]);
        }
    }, [contentDisplayed, modalMenuContext]);

    /**
     * Fonction helper pour créer les onClick avec l'état actif
     */
    const createMenuItemClick = (targetContent: AvailableItems) => () => {
        setNewContentToDisplay({ contentToDisplayOnRender: targetContent });
    };

    const customMenuItems: MenuItemProps[] = [
        {
            identifier: 'account_details',
            label: 'Mon compte',
            onClick: createMenuItemClick('account_details'),
            icon: { icon: ['fasl', 'user-circle'], size: 'sm' },
            active: contentDisplayed === 'account_details',
        },
        {
            identifier: 'preferences',
            label: 'Mes préférences',
            icon: { icon: ['fasl', 'sliders'], size: 'sm' },
            childrenItems: [
                {
                    identifier: 'themes',
                    label: 'Thèmes',
                    onClick: createMenuItemClick('themes'),
                    icon: {
                        icon: ['fasl', 'paper-plane'],
                        sx: { display: 'none' },
                    },
                    active: contentDisplayed === 'themes',
                },
                // {
                //     identifier: 'notifications',
                //     label: 'Notifications',
                //     onClick: createMenuItemClick('notifications'),
                //     icon: {
                //         icon: ['fasl', 'paper-plane'],
                //         sx: { display: 'none' },
                //     },
                //     active: contentDisplayed === 'notifications',
                // },
                {
                    identifier: 'groups_societes',
                    label: 'Mes groupes de sociétés',
                    onClick: createMenuItemClick('groups_societes'),
                    icon: {
                        icon: ['fasl', 'paper-plane'],
                        sx: { display: 'none' },
                    },
                    active: contentDisplayed === 'groups_societes',
                },
            ],
        },
    ];

    /**
     * Composant de chargement
     */
    const LoadingContent = function () {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flex: 1,
                    minHeight: '500px',
                    width: '100%',
                }}
            >
                <CenterCircularProgress />
            </div>
        );
    };

    const emptyBoxStyle: SxProps = {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        width: '100%',
    };

    const renderCurrentSelection = () => {
        if (contentState === 'loading' || isTransitioning) {
            return <LoadingContent />;
        }

        if (!contentDisplayed) {
            return (
                <EmptyBox
                    text="Aucun contenu disponible"
                    sx={emptyBoxStyle}
                />
            );
        }

        switch (contentDisplayed) {
            case 'account_details':
                if (isUserLoading) {
                    return <LoadingContent />;
                }
                return (
                    <UserAccountDetails
                        prenom={user?.prenom ?? undefined}
                        nom={user?.nom ?? undefined}
                        email={user?.mail ?? undefined}
                        telephone={user?.ligneDirecte ?? undefined}
                        manageAvatar={<ManageAvatar />}
                    />
                );
            case 'themes':
                return <ThemePreferences />;
            case 'notifications':
                return <NotificationsPreferences />;
            case 'groups_societes':
                return <SocieteGroupList />;
            default:
                return (
                    <EmptyBox
                        text="Aucun contenu disponible"
                        sx={emptyBoxStyle}
                    />
                );
        }
    };

    return {
        customMenuItems,
        setNewContentToDisplay,
        renderCurrentSelection,
        isLoading: contentState === 'loading' || isTransitioning,
        contentState,
        activeItem: contentDisplayed,
    };
};
